import ControlledTable from "@/manager/components/controlled-table/controlled-table";
import { useState } from "react";
import { IDataset, IDatasetLabel } from "./dataset-edit";
import { noop } from "@/manager/utils";
import SimpleModal from "@/shared/components/simple-modal/simple-modal";
import DatasetLabelModal from "./dataset-label-modal";

export interface IDatasetLabelsTabProps {
  data: IDataset;
  setData: (data: IDataset) => void;
  possibleLabels: IDatasetLabel[];
  hasLinkedAlert: boolean;
}

const emptyDatasetLabel = {
  id: "",
  amount_direction_id: "",
  datamodel_field_id: "",
  datamodel_id: "",
  datamodel_type: "",
  datamodel_reference_field_id: "",
  datamodel_referencing_component_group_id: "",
  label: "",
  negative: "",
  positive: "",
  datamodel_field_type: "",
  field_name: "",
  distinct_values: 0,
  empty_values: 0,
  null_values: 0,
  total_rows: 0
};
const mergeFieldStats = (label: IDatasetLabel, possibleLabels: IDatasetLabel[]): IDatasetLabel => {
  const match = possibleLabels.find(pl => {
    if (pl.field_name !== label.field_name) {
      return false;
    }
    if (label.datamodel_referencing_component_group_id &&
        pl.datamodel_referencing_component_group_id) {
      return pl.datamodel_referencing_component_group_id ===
             label.datamodel_referencing_component_group_id;
    }
    return pl.datamodel_field_id === label.datamodel_field_id;
  });

  return {
    ...label,
    null_values: match?.null_values ?? label.null_values,
    empty_values: match?.empty_values ?? label.empty_values,
    distinct_values: match?.distinct_values ?? label.distinct_values,
    total_rows: match?.total_rows ?? label.total_rows ?? 0,
  };
};

const DatasetLabelsTab = ({ data, setData, possibleLabels, hasLinkedAlert }: IDatasetLabelsTabProps) => {
  const [showEditFieldModal, setShowEditFieldModal] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<IDatasetLabel>({...emptyDatasetLabel});
  
  const handleOnDeleted = (deletedIds: string[]) => {
    const newLabels = data.labels.filter(row => !deletedIds.includes(row.id));
    setData({
      ...data,
      labels: newLabels
    });
  };

  const getLeftoverLabels = () => {
    const selectedLabelIds = data.labels.map(label => label.id);
    return possibleLabels.filter(label => !selectedLabelIds.includes(label.id));
  };

  const getAmountDirectionCandidates = () => {
    return data.labels.filter(label => label.label === "amount_direction");
  };

  const renderModalBody = () => {
    return <DatasetLabelModal 
      selectableFields={getLeftoverLabels()} 
      selectedData={selectedRowData}
      onSelectedDataChanged={setSelectedRowData}
      amountDirectionCandidates={getAmountDirectionCandidates()}
    />
  };

  const handleModalSave = () => {
    const newLabels = [...data.labels];
    const existingLabelIndex = data.labels.findIndex(label => label.id === selectedRowData.id);
    if (existingLabelIndex >= 0) {
      newLabels[existingLabelIndex] = selectedRowData;
    } else {
      newLabels.push({
        ...selectedRowData
      });
    }
    setData({
      ...data,
      labels: newLabels
    });    
    setSelectedRowData({...emptyDatasetLabel});
    setShowEditFieldModal(false);
  }

  const renderBannerMessage = () => {
    const hasEntity = data.labels.some(label => label.label === "entity");
    const hasDatetime = data.labels.some(label => label.label === "datetime");
    const hasAmount = data.labels.some(label => label.label === "amount");
    const hasSuspicious = data.labels.some(label => label.label === "suspicious");
    if (hasEntity && hasDatetime && hasAmount && (!hasLinkedAlert ? (hasSuspicious) : true)) {
      return;
    }
    return <span>To generate models, please create the following labels:
      {!hasSuspicious && !hasLinkedAlert && <b>suspicious </b>}
      {!hasEntity && <b>entity </b>}
      {!hasAmount && <b>amount </b>}
      {!hasDatetime && <b>datetime </b>}
    </span>;
  }

  return <div className="datasetLabelsTab">
    {renderBannerMessage()}
    <SimpleModal
      modalBody={renderModalBody()} 
      mode={"Dataset Label"}
      handleModalClosed={() => {
        setSelectedRowData({...emptyDatasetLabel});
        setShowEditFieldModal(false);
      }}
      handleSubmit={handleModalSave}
      show={showEditFieldModal}
      buttonText="Save"
    />
    <ControlledTable
      data={data.labels.map(label => mergeFieldStats(label, possibleLabels))}
      columns={[
        { 
          key: "field_name",
          displayName: "Field Name",
          dataType: { type: "text" },
          sortable: false
        },
        {
          key: "empty_values",
          displayName: "Empty values",
          dataType: { type: "text" },
          sortable: false
        },
        {
          key: "null_values",
          displayName: "Null values",
          dataType: { type: "text" },
          sortable: false
        },
        {
          key: "distinct_values",
          displayName: "Unique values",
          dataType: { type: "text" },
          sortable: false
        },
        {
          key: "total_rows",
          displayName: "Total rows",
          dataType: { type: "text" },
          sortable: false
        },
        { 
          key: "datamodel_field_type",
          displayName: "Field datatype",
          dataType: { type: "enumeration", values: [
            { id: "text", name: "Text" },
            { id: "datetime", name: "Datetime" },
            { id: "boolean", name: "Boolean" },
            { id: "number", name: "Number" },
            { id: "float", name: "Float" },
          ]},
          sortable: false
        },
        { 
          key: "label",
          displayName: "Label",
          dataType: { type: "enumeration", values: [
            { id: "amount", name: "Amount" },
            { id: "amount_direction", name: "Amount direction" },
            { id: "category", name: "Category" },
            { id: "datetime", name: "DateTime" },
            { id: "entity", name: "Entity" },
            { id: "alert", name: "Alerted" }
          ]},
          sortable: false
        }
      ]}
      onRowClick={(id: string) => {
        const rowData = data.labels.find(row => row.id === id);
        if (!rowData) {
          return;
        }
        setSelectedRowData(mergeFieldStats(rowData, possibleLabels));
        setShowEditFieldModal(true);
      }}
      selectionMode="multi"
      onNewClicked={() => setShowEditFieldModal(true)}
      onScroll={noop}
      onSorted={noop}
      onDeleted={handleOnDeleted}
    />
  </div>;
};

export default DatasetLabelsTab;